import { useMemo, useState } from "react";

import GameLauncher from "../shared/GameLauncher";
import GameProgress from "../shared/GameProgress";
import GameResults from "../shared/GameResults";
import GameInterpretation from "../shared/GameInterpretation.jsx";
import { getBasicInterpretation } from "../shared/gameInterpretation";

import GameFields from "../shared/GameFields";
import resolveGameConfig from "../shared/resolveGameConfig";

import {
  calculateProgress,
  calculateAverageReactionTime,
  calculateTotalTime,
  calculateSelectionAccuracy,
  calculateErrorCount,
} from "../shared/gameMetrics";

import "./NumberCombination.css";

const GAME_CONFIG = {
  easy: {
    label: "Fácil",
    totalRounds: 8,
    itemCount: 5,
    minValue: 1,
    maxValue: 50,
    targetCount: 2,
    allowThreeNumbers: false,
  },

  medium: {
    label: "Media",
    totalRounds: 10,
    itemCount: 6,
    minValue: 5,
    maxValue: 120,
    targetCount: 2,
    allowThreeNumbers: true,
  },

  hard: {
    label: "Difícil",
    totalRounds: 12,
    itemCount: 7,
    minValue: 10,
    maxValue: 350,
    targetCount: 3,
    allowThreeNumbers: true,
  },
};

const getRandomInteger = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const shuffleArray = (items) => {
  const shuffled = [...items];

  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));

    [shuffled[index], shuffled[randomIndex]] = [
      shuffled[randomIndex],
      shuffled[index],
    ];
  }

  return shuffled;
};

const getCombinations = (items, combinationSize) => {
  const combinations = [];

  const createCombination = (startIndex, currentCombination) => {
    if (currentCombination.length === combinationSize) {
      combinations.push([...currentCombination]);
      return;
    }

    for (
      let index = startIndex;
      index < items.length;
      index += 1
    ) {
      currentCombination.push(index);
      createCombination(index + 1, currentCombination);
      currentCombination.pop();
    }
  };

  createCombination(0, []);

  return combinations;
};

const hasUniqueSolution = ({
  values,
  target,
  targetCount,
  solutionIndexes,
}) => {
  const validCombinations = getCombinations(
    values,
    targetCount
  ).filter((combination) => {
    const sum = combination.reduce(
      (total, index) => total + values[index],
      0
    );

    return sum === target;
  });

  if (validCombinations.length !== 1) {
    return false;
  }

  const expected = [...solutionIndexes].sort(
    (first, second) => first - second
  );

  const found = [...validCombinations[0]].sort(
    (first, second) => first - second
  );

  return expected.every(
    (index, position) => index === found[position]
  );
};

const createRound = ({
  itemCount,
  minValue,
  maxValue,
  targetCount,
}) => {
  const safeItemCount = Math.max(
    Number(itemCount) || 5,
    Number(targetCount) + 2
  );

  const safeTargetCount = Math.max(
    2,
    Math.min(Number(targetCount) || 2, 3)
  );

  for (let attempt = 0; attempt < 150; attempt += 1) {
    const solutionValues = [];

    while (solutionValues.length < safeTargetCount) {
      const candidate = getRandomInteger(
        minValue,
        maxValue
      );

      if (!solutionValues.includes(candidate)) {
        solutionValues.push(candidate);
      }
    }

    const target = solutionValues.reduce(
      (total, value) => total + value,
      0
    );

    const distractors = [];

    while (
      distractors.length <
      safeItemCount - safeTargetCount
    ) {
      const candidate = getRandomInteger(
        minValue,
        maxValue
      );

      if (
        !solutionValues.includes(candidate) &&
        !distractors.includes(candidate)
      ) {
        distractors.push(candidate);
      }
    }

    const mixedItems = shuffleArray([
      ...solutionValues.map((value) => ({
        value,
        isSolution: true,
      })),

      ...distractors.map((value) => ({
        value,
        isSolution: false,
      })),
    ]);

    const values = mixedItems.map((item) => item.value);

    const solutionIndexes = mixedItems
      .map((item, index) =>
        item.isSolution ? index : null
      )
      .filter((index) => index !== null);

    if (
      hasUniqueSolution({
        values,
        target,
        targetCount: safeTargetCount,
        solutionIndexes,
      })
    ) {
      return {
        id: `${Date.now()}-${Math.random()}`,
        target,
        values,
        solutionIndexes,
        targetCount: safeTargetCount,
      };
    }
  }

  const fallbackSolution = Array.from(
    { length: safeTargetCount },
    (_, index) => minValue + index + 2
  );

  const fallbackDistractors = Array.from(
    {
      length: safeItemCount - safeTargetCount,
    },
    (_, index) =>
      maxValue + (index + 1) * (safeTargetCount + 3)
  );

  const mixedItems = shuffleArray([
    ...fallbackSolution.map((value) => ({
      value,
      isSolution: true,
    })),

    ...fallbackDistractors.map((value) => ({
      value,
      isSolution: false,
    })),
  ]);

  return {
    id: `${Date.now()}-${Math.random()}`,
    target: fallbackSolution.reduce(
      (total, value) => total + value,
      0
    ),
    values: mixedItems.map((item) => item.value),
    solutionIndexes: mixedItems
      .map((item, index) =>
        item.isSolution ? index : null
      )
      .filter((index) => index !== null),
    targetCount: safeTargetCount,
  };
};

function NumberCombination() {
  const [selectedDifficulty, setSelectedDifficulty] =
    useState(null);

  const [config, setConfig] = useState(null);

  const [roundIndex, setRoundIndex] = useState(0);
  const [round, setRound] = useState(null);

  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);

  const [selectedIndexes, setSelectedIndexes] =
    useState([]);

  const [correctSelections, setCorrectSelections] =
    useState(0);

  const [wrongSelections, setWrongSelections] =
    useState(0);

  const [omissions, setOmissions] = useState(0);
  const [perfectRounds, setPerfectRounds] = useState(0);

  const [responses, setResponses] = useState([]);

  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [roundStartTime, setRoundStartTime] =
    useState(null);

  const totalRounds = config?.totalRounds || 0;

  const progress = useMemo(
    () => calculateProgress(roundIndex, totalRounds),
    [roundIndex, totalRounds]
  );

  const accuracy = calculateSelectionAccuracy({
    correctSelections,
    wrongSelections,
    omissions,
  });

  const errors = calculateErrorCount(
    wrongSelections,
    omissions
  );

  const averageReactionTime =
    calculateAverageReactionTime(responses);

  const totalTime = calculateTotalTime(
    startTime,
    endTime
  );

  const interpretation = finished
    ? getBasicInterpretation({
        accuracy,
        errors,
        averageReactionTime,
        difficulty: selectedDifficulty,
      })
    : null;

  const getTargetCountForRound = (
    nextConfig,
    nextRoundIndex
  ) => {
    if (!nextConfig.allowThreeNumbers) {
      return Number(nextConfig.targetCount) || 2;
    }

    if (
      Number(nextConfig.targetCount) === 3 &&
      !nextConfig.mixTargetCount
    ) {
      return 3;
    }

    if (nextConfig.mixTargetCount === false) {
      return Number(nextConfig.targetCount) || 2;
    }

    if (nextConfig.difficultyKey === "medium") {
      const midpoint = Math.ceil(
        nextConfig.totalRounds / 2
      );

      return nextRoundIndex < midpoint ? 2 : 3;
    }

    return nextRoundIndex % 2 === 0 ? 2 : 3;
  };

  const generateRound = (
    nextConfig,
    nextRoundIndex
  ) => {
    const targetCount = getTargetCountForRound(
      nextConfig,
      nextRoundIndex
    );

    return createRound({
      itemCount: Number(nextConfig.itemCount),
      minValue: Number(nextConfig.minValue),
      maxValue: Number(nextConfig.maxValue),
      targetCount,
    });
  };

  const startGame = (
    difficultyOrConfig =
      selectedDifficulty || "easy"
  ) => {
    const {
      difficultyKey,
      config: resolvedConfig,
    } = resolveGameConfig(
      difficultyOrConfig,
      GAME_CONFIG
    );

    const nextConfig = {
      ...resolvedConfig,
      difficultyKey,
      totalRounds:
        Number(resolvedConfig.totalRounds) || 8,
      itemCount:
        Number(resolvedConfig.itemCount) || 5,
      minValue:
        Number(resolvedConfig.minValue) || 1,
      maxValue:
        Number(resolvedConfig.maxValue) || 50,
      targetCount:
        Number(resolvedConfig.targetCount) || 2,
      allowThreeNumbers:
        resolvedConfig.allowThreeNumbers ??
        Number(resolvedConfig.targetCount) === 3,
      mixTargetCount:
        resolvedConfig.mixTargetCount ?? false,
    };

    setSelectedDifficulty(difficultyKey);
    setConfig(nextConfig);

    setRoundIndex(0);
    setRound(generateRound(nextConfig, 0));

    setStarted(true);
    setFinished(false);

    setSelectedIndexes([]);

    setCorrectSelections(0);
    setWrongSelections(0);
    setOmissions(0);
    setPerfectRounds(0);

    setResponses([]);

    const now = Date.now();

    setStartTime(now);
    setEndTime(null);
    setRoundStartTime(now);
  };

  const toggleNumber = (index) => {
    if (!round) {
      return;
    }

    setSelectedIndexes((currentIndexes) => {
      if (currentIndexes.includes(index)) {
        return currentIndexes.filter(
          (selectedIndex) => selectedIndex !== index
        );
      }

      if (
        currentIndexes.length >= round.targetCount
      ) {
        return currentIndexes;
      }

      return [...currentIndexes, index];
    });
  };

  const finishGame = () => {
    setEndTime(Date.now());
    setFinished(true);
    setStarted(false);
  };

  const confirmSelection = () => {
    if (
      !round ||
      selectedIndexes.length !== round.targetCount
    ) {
      return;
    }

    const responseTime =
      Date.now() - roundStartTime;

    const selectedCorrectly =
      selectedIndexes.filter((index) =>
        round.solutionIndexes.includes(index)
      ).length;

    const selectedIncorrectly =
      selectedIndexes.filter(
        (index) =>
          !round.solutionIndexes.includes(index)
      ).length;

    const roundOmissions =
      round.solutionIndexes.filter(
        (index) =>
          !selectedIndexes.includes(index)
      ).length;

    const isPerfectRound =
      selectedCorrectly === round.targetCount &&
      selectedIncorrectly === 0 &&
      roundOmissions === 0;

    setCorrectSelections(
      (current) => current + selectedCorrectly
    );

    setWrongSelections(
      (current) => current + selectedIncorrectly
    );

    setOmissions(
      (current) => current + roundOmissions
    );

    if (isPerfectRound) {
      setPerfectRounds(
        (current) => current + 1
      );
    }

    setResponses((current) => [
      ...current,
      responseTime,
    ]);

    const nextRoundIndex = roundIndex + 1;

    if (nextRoundIndex >= totalRounds) {
      finishGame();
      return;
    }

    setRoundIndex(nextRoundIndex);
    setSelectedIndexes([]);

    setRound(
      generateRound(config, nextRoundIndex)
    );

    setRoundStartTime(Date.now());
  };

  const selectedSum = selectedIndexes.reduce(
    (total, index) =>
      total + (round?.values[index] || 0),
    0
  );

  if (!started && !finished) {
    return (
      <GameLauncher
        title="Combinación de números"
        description="Selecciona los números que, al sumarlos, dan como resultado el número objetivo."
        areas={[
          "Flexibilidad cognitiva",
          "Memoria de trabajo",
          "Cálculo mental",
          "Planificación",
        ]}
        defaultDifficulty="easy"
        customFields={[
          GameFields.totalRounds({
            min: 4,
            max: 30,
            defaultValue: 10,
          }),

          GameFields.itemCount({
            label: "Números disponibles",
            min: 4,
            max: 10,
            defaultValue: 6,
          }),

          GameFields.targetCount({
            label: "Números que debe seleccionar",
            min: 2,
            max: 3,
            defaultValue: 2,
          }),

          {
            key: "minValue",
            type: "number",
            label: "Número mínimo",
            min: 1,
            max: 500,
            defaultValue: 5,
          },

          {
            key: "maxValue",
            type: "number",
            label: "Número máximo",
            min: 10,
            max: 1000,
            defaultValue: 150,
          },
        ]}
        onStart={startGame}
      />
    );
  }

  if (finished) {
    return (
      <div className="number-combination-page">
        <div className="number-combination-results">
          <GameResults
            title="Resultados"
            correctAnswers={perfectRounds}
            totalAnswers={totalRounds}
            accuracy={accuracy}
            errors={errors}
            omissions={omissions}
            averageReactionTime={
              averageReactionTime
            }
            totalTime={totalTime}
            onRestart={() =>
              startGame(
                selectedDifficulty || "easy"
              )
            }
          />

          <div className="number-combination-results-grid">
            <div className="number-combination-result-card">
              <span>Rondas perfectas</span>
              <strong>
                {perfectRounds}/{totalRounds}
              </strong>
            </div>

            <div className="number-combination-result-card">
              <span>Selecciones correctas</span>
              <strong>{correctSelections}</strong>
            </div>

            <div className="number-combination-result-card">
              <span>Selecciones incorrectas</span>
              <strong>{wrongSelections}</strong>
            </div>

            <div className="number-combination-result-card">
              <span>Omisiones</span>
              <strong>{omissions}</strong>
            </div>
          </div>

          <GameInterpretation
            interpretation={interpretation}
          />

          <button
            type="button"
            className="number-combination-restart"
            onClick={() =>
              startGame(
                selectedDifficulty || "easy"
              )
            }
          >
            Jugar de nuevo
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="number-combination-page">
      <main className="number-combination-card">
        <header className="number-combination-header">
          <div>
            <p className="number-combination-area">
              Flexibilidad cognitiva
            </p>

            <h1>Combinación de números</h1>

            <p>
              Selecciona {round?.targetCount} números
              cuya suma sea igual al resultado.
            </p>
          </div>

          <div className="number-combination-score">
            <span>Rondas perfectas</span>
            <strong>{perfectRounds}</strong>
          </div>
        </header>

        <GameProgress
          currentRound={roundIndex + 1}
          totalRounds={totalRounds}
          progress={progress}
        />

        <section className="number-combination-game">
          <div className="number-combination-target">
            <span>Resultado</span>
            <strong>{round?.target}</strong>
          </div>

          <div className="number-combination-instruction">
            Elige exactamente{" "}
            <strong>{round?.targetCount}</strong>{" "}
            números.
          </div>

          <div
            className="number-combination-grid"
            style={{
              gridTemplateColumns: `repeat(${Math.min(
                round?.values.length || 4,
                4
              )}, minmax(90px, 1fr))`,
            }}
          >
            {round?.values.map((value, index) => {
              const isSelected =
                selectedIndexes.includes(index);

              return (
                <button
                  type="button"
                  key={`${round.id}-${index}`}
                  className={`number-combination-item ${
                    isSelected
                      ? "number-combination-item-selected"
                      : ""
                  }`}
                  onClick={() => toggleNumber(index)}
                  aria-pressed={isSelected}
                >
                  {value}
                </button>
              );
            })}
          </div>

          <div className="number-combination-selection">
            <div>
              <span>Seleccionados</span>

              <strong>
                {selectedIndexes.length}/
                {round?.targetCount}
              </strong>
            </div>

            <div>
              <span>Suma actual</span>
              <strong>{selectedSum}</strong>
            </div>
          </div>

          <button
            type="button"
            className="number-combination-confirm"
            onClick={confirmSelection}
            disabled={
              selectedIndexes.length !==
              round?.targetCount
            }
          >
            Confirmar respuesta
          </button>
        </section>
      </main>
    </div>
  );
}

export default NumberCombination;